/**
 * Minimal Field Guide - Table of Contents
 * Clean, professional left-side TOC for blog posts
 * Focuses on functionality over theming
 */

class FieldGuideTOC {
  constructor(options = {}) {
    this.options = {
      // Selectors - targeting blog/article content
      contentSelector: options.contentSelector || '.article__content, .article-content, .blog-article, .post-content, .rte, .content, [data-article-content], .blog-post-content, main article, .shopify-policy__body',
      headingSelector: options.headingSelector || 'h2',
      
      // Settings
      minHeadings: options.minHeadings || 2,
      smoothScroll: options.smoothScroll !== false,
      offset: options.offset || 100,
      
      // Responsive
      mobileBreakpoint: options.mobileBreakpoint || 768,
      
      // Performance
      throttleDelay: options.throttleDelay || 100,
      
      ...options
    };
    
    this.isMobile = window.innerWidth <= this.options.mobileBreakpoint;
    this.headings = [];
    this.tocElement = null;
    this.toggleButton = null;
    this.isVisible = false;
    this.activeIndex = -1;
    
    // Throttled scroll handler
    this.handleScroll = this.throttle(this.updateActiveHeading.bind(this), this.options.throttleDelay);
    
    this.init();
  }
  
  /**
   * Initialize the Field Guide TOC
   */
  async init() {
    try {
      // Only run on blog posts/articles
      if (!this.isBlogPost()) {
        return;
      }
      
      await this.waitForContent();
      this.findHeadings();
      
      if (this.headings.length >= this.options.minHeadings) {
        this.createTOC();
        this.createToggleButton();
        this.bindEvents();
        this.addReadingProgress();
      }
    } catch (error) {
      console.warn('Field Guide TOC initialization failed:', error);
    }
  }
  
  /**
   * Check if current page is a blog post/article
   */
  isBlogPost() {
    const indicators = [
      'blog',
      'article',
      'post',
      'news'
    ];
    
    const url = window.location.pathname.toLowerCase();
    const bodyClasses = document.body.className.toLowerCase();
    const pageTitle = document.title.toLowerCase();
    
    return indicators.some(indicator => 
      url.includes(indicator) || 
      bodyClasses.includes(indicator) ||
      pageTitle.includes(indicator)
    ) || document.querySelector('.article, .blog-post, .post-content, [data-article-content]');
  }
  
  /**
   * Wait for content to be available
   */
  waitForContent() {
    return new Promise((resolve) => {
      const checkContent = () => {
        const contentArea = document.querySelector(this.options.contentSelector);
        if (contentArea && contentArea.innerHTML.trim()) {
          resolve();
        } else {
          setTimeout(checkContent, 100);
        }
      };
      checkContent();
    });
  }
  
  /**
   * Find and process headings
   */
  findHeadings() {
    const contentArea = document.querySelector(this.options.contentSelector);
    if (!contentArea) return;
    
    const headingElements = contentArea.querySelectorAll(this.options.headingSelector);
    
    this.headings = Array.from(headingElements).map((heading, index) => {
      // Generate ID if not present
      if (!heading.id) {
        heading.id = this.generateId(heading.textContent);
      }
      
      return {
        element: heading,
        id: heading.id,
        text: heading.textContent.trim(),
        level: parseInt(heading.tagName.charAt(1)),
        index: index,
        completed: false
      };
    });
  }
  
  /**
   * Generate a URL-safe ID from text
   */
  generateId(text) {
    return text
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .substring(0, 50);
  }
  
  /**
   * Create the TOC structure
   */
  createTOC() {
    // Create main container
    this.tocElement = document.createElement('nav');
    this.tocElement.className = 'field-guide-toc';
    this.tocElement.setAttribute('aria-label', 'Field Guide - Table of Contents');
    
    // Create header
    const header = document.createElement('div');
    header.className = 'field-guide-header';
    header.innerHTML = `
      <h2 class="field-guide-title">Field Guide</h2>
      <p class="field-guide-subtitle">Navigate this article</p>
      <button class="field-guide-close" aria-label="Close field guide"></button>
    `;
    
    // Create content area
    const content = document.createElement('div');
    content.className = 'field-guide-content';
    
    // Create list
    const list = document.createElement('ul');
    list.className = 'field-guide-list';
    
    // Add headings to list
    this.headings.forEach((heading, index) => {
      const listItem = document.createElement('li');
      listItem.className = 'field-guide-item';
      
      const link = document.createElement('a');
      link.className = 'field-guide-link';
      link.href = `#${heading.id}`;
      link.setAttribute('data-index', index);
      link.innerHTML = `
        <span class="field-guide-icon"></span>
        <span class="field-guide-text">${heading.text}</span>
      `;
      
      listItem.appendChild(link);
      list.appendChild(listItem);
    });
    
    content.appendChild(list);
    this.tocElement.appendChild(header);
    this.tocElement.appendChild(content);
    
    // Add to page
    document.body.appendChild(this.tocElement);
  }
  
  /**
   * Create toggle button
   */
  createToggleButton() {
    this.toggleButton = document.createElement('button');
    this.toggleButton.className = 'field-guide-toggle';
    this.toggleButton.setAttribute('aria-label', 'Open field guide');
    
    document.body.appendChild(this.toggleButton);
  }
  
  /**
   * Bind all event listeners
   */
  bindEvents() {
    // Toggle button click
    this.toggleButton?.addEventListener('click', (e) => {
      e.preventDefault();
      this.toggle();
    });
    
    // Close button click
    const closeButton = this.tocElement.querySelector('.field-guide-close');
    closeButton?.addEventListener('click', (e) => {
      e.preventDefault();
      this.hide();
    });
    
    // Link clicks
    const links = this.tocElement.querySelectorAll('.field-guide-link');
    links.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const index = parseInt(link.getAttribute('data-index'));
        this.scrollToHeading(index);
        if (this.isMobile) {
          this.hide();
        }
      });
    });
    
    // Scroll tracking
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    
    // Backdrop click (mobile)
    this.tocElement.addEventListener('click', (e) => {
      if (e.target === this.tocElement) {
        this.hide();
      }
    });
    
    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isVisible) {
        this.hide();
      }
    });
    
    // Resize handler
    window.addEventListener('resize', this.throttle(() => {
      this.isMobile = window.innerWidth <= this.options.mobileBreakpoint;
    }, 250));
  }
  
  /**
   * Show/hide toggle
   */
  toggle() {
    if (this.isVisible) {
      this.hide();
    } else {
      this.show();
    }
  }
  
  /**
   * Show TOC
   */
  show() {
    this.tocElement.classList.add('active', 'entering');
    this.toggleButton?.classList.add('active');
    this.isVisible = true;
    
    // Remove entering class after animation
    setTimeout(() => {
      this.tocElement.classList.remove('entering');
    }, 300);
    
    // Update active heading
    this.updateActiveHeading();
  }
  
  /**
   * Hide TOC
   */
  hide() {
    this.tocElement.classList.remove('active', 'entering');
    this.toggleButton?.classList.remove('active');
    this.isVisible = false;
  }
  
  /**
   * Scroll to specific heading
   */
  scrollToHeading(index) {
    if (index < 0 || index >= this.headings.length) return;
    
    const heading = this.headings[index];
    const element = heading.element;
    
    if (this.options.smoothScroll) {
      const targetPosition = element.getBoundingClientRect().top + window.pageYOffset - this.options.offset;
      
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
    } else {
      element.scrollIntoView();
      window.scrollBy(0, -this.options.offset);
    }
    
    // Update URL hash
    if (history.replaceState) {
      history.replaceState(null, null, `#${heading.id}`);
    }
  }
  
  /**
   * Update active heading based on scroll position
   */
  updateActiveHeading() {
    const scrollPosition = window.pageYOffset + this.options.offset + 50;
    let activeIndex = -1;
    
    // Check if we're at the bottom of the page
    const isAtBottom = (window.innerHeight + window.pageYOffset) >= (document.body.offsetHeight - 100);
    
    // Find the heading that's currently in view
    for (let i = this.headings.length - 1; i >= 0; i--) {
      const headingTop = this.headings[i].element.getBoundingClientRect().top + window.pageYOffset;
      
      // Make checkmarks appear earlier by adding buffer (about 150px or ~3-4 lines earlier)
      if (scrollPosition >= (headingTop - 150)) {
        activeIndex = i;
        break;
      }
    }
    
    // If at bottom of page, make sure the last heading is active
    if (isAtBottom && activeIndex < this.headings.length - 1) {
      activeIndex = this.headings.length - 1;
    }
    
    // Always update states to ensure proper clearing (especially when scrolling back up)
    const links = this.tocElement.querySelectorAll('.field-guide-link');
    
    // Update active index
    this.activeIndex = activeIndex;
    
    links.forEach((link, index) => {
      // Set active state
      link.classList.toggle('active', index === activeIndex);
      
      // Always clear completed state first
      link.classList.remove('completed');
      
      // Set completed state based on scroll position
      if (isAtBottom) {
        // When at bottom, mark all headings as completed
        if (index < this.headings.length) {
          link.classList.add('completed');
        }
      } else {
        // Normal scrolling: only mark as completed if we've scrolled past it
        if (index < activeIndex) {
          link.classList.add('completed');
        }
      }
    });
  }
  
  /**
   * Add reading progress tracking
   */
  addReadingProgress() {
    // Mark sections as completed when user scrolls past them
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && entry.boundingClientRect.top < 100) {
          const headingIndex = this.headings.findIndex(h => h.element === entry.target);
          if (headingIndex > -1) {
            this.headings[headingIndex].completed = true;
          }
        }
      });
    }, {
      rootMargin: '-100px 0px -50% 0px'
    });
    
    this.headings.forEach(heading => {
      observer.observe(heading.element);
    });
  }
  
  /**
   * Throttle function for performance
   */
  throttle(func, delay) {
    let timeoutId;
    let lastExecTime = 0;
    
    return function (...args) {
      const currentTime = Date.now();
      
      if (currentTime - lastExecTime > delay) {
        func.apply(this, args);
        lastExecTime = currentTime;
      } else {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          func.apply(this, args);
          lastExecTime = Date.now();
        }, delay - (currentTime - lastExecTime));
      }
    };
  }
  
  /**
   * Cleanup method
   */
  destroy() {
    if (this.tocElement) {
      this.tocElement.remove();
    }
    if (this.toggleButton) {
      this.toggleButton.remove();
    }
    
    window.removeEventListener('scroll', this.handleScroll);
    this.headings = [];
    this.tocElement = null;
    this.toggleButton = null;
  }
}

// Auto-initialize when DOM is ready (blog posts only)
(function() {
  'use strict';
  
  function initFieldGuide() {
    if (window.fieldGuideTOC) {
      window.fieldGuideTOC.destroy();
    }
    
    window.fieldGuideTOC = new FieldGuideTOC({
      // Customize options here if needed
      minHeadings: 2,
      smoothScroll: true,
      offset: 100
    });
  }
  
  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFieldGuide);
  } else {
    initFieldGuide();
  }
  
  // Re-initialize on navigation (for SPAs)
  let lastUrl = location.href;
  new MutationObserver(() => {
    const url = location.href;
    if (url !== lastUrl) {
      lastUrl = url;
      setTimeout(initFieldGuide, 100);
    }
  }).observe(document, { subtree: true, childList: true });
})();
